import React, { useState, useEffect, useCallback } from 'react';
import { satisfaction as api } from '../services/api';

const CANAUX = [
  { cle: 'telephone', label: '📞 Téléphone', emet: false },
  { cle: 'sms', label: '📱 SMS', emet: true },
  { cle: 'mail', label: '✉️ Mail', emet: true },
  { cle: 'sur_place', label: '🏢 Sur place', emet: false },
];

const CANAL_LABEL = { telephone: 'téléphone', sms: 'SMS', mail: 'mail', sur_place: 'sur place', note: 'note' };

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

const FINALITE_LABEL = { prospection_tel: '📞 Téléphone', prospection_sms: '📱 SMS', prospection_email: '✉️ E-mail' };

// Bloc « Recontact » : les cases cochées, et leur sort chez A97 RGPD (qui
// détient la preuve).
function recontactLisible(r, consentements = []) {
  const choix = r.valeur_json?.choix || {};
  const codes = Object.keys(choix);
  if (!codes.length) return <span className="sat-vide-inline">Sans réponse</span>;
  const lignes = consentements.filter((c) => c.choix && Object.keys(c.choix).length);
  return (
    <div className="sat-recontact">
      <div>
        {codes.map((code) => (
          <span key={code} className={choix[code] ? 'status-success' : 'sat-vide-inline'}>
            {FINALITE_LABEL[code] || code} {choix[code] ? '✓' : '—'}
          </span>
        ))}
      </div>
      {lignes.map((c) => (
        <div key={c.id} className="sat-meta">
          {Object.keys(c.choix).map((k) => FINALITE_LABEL[k] || k).join(' + ')} :{' '}
          {c.statut === 'enregistre' && (c.preuve_url
            ? <a href={c.preuve_url} target="_blank" rel="noopener noreferrer">✓ preuve A97 RGPD</a>
            : <span className="status-success">✓ enregistré dans A97 RGPD</span>)}
          {c.statut === 'a_envoyer' && (
            <span className="status-warning" title={c.erreur || ''}>
              ⏳ en attente de transmission à A97 RGPD ({c.tentatives} tentative{c.tentatives > 1 ? 's' : ''})
            </span>
          )}
          {c.statut === 'echec' && <span className="status-error">✕ refusé par A97 RGPD : {c.erreur}</span>}
        </div>
      ))}
    </div>
  );
}

function valeurLisible(r) {
  if (r.valeur_bool !== null && r.valeur_bool !== undefined) {
    return <span className={`status-${r.valeur_bool ? 'success' : 'error'}`}>{r.valeur_bool ? 'OUI' : 'NON'}</span>;
  }
  if (r.valeur_num !== null && r.valeur_num !== undefined) {
    const n = Number(r.valeur_num);
    if (r.type === 'nps') {
      const cls = n >= 9 ? 'status-success' : n >= 7 ? 'status-warning' : 'status-error';
      return <span className={cls}>{n}/10</span>;
    }
    return <span>{'★'.repeat(n)}{'☆'.repeat(Math.max(0, 5 - n))} ({n}/5)</span>;
  }
  if (r.valeur_texte) return <span className="sat-verbatim">{r.valeur_texte}</span>;
  return <span className="sat-vide-inline">Sans réponse</span>;
}

export default function SatisfactionReponseModal({ envoiId, user, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');
  const [canal, setCanal] = useState('telephone');
  const [note, setNote] = useState('');
  const [objet, setObjet] = useState('');
  const [message, setMessage] = useState('');
  const [brouillonIa, setBrouillonIa] = useState(false);
  const [redaction, setRedaction] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const isSuperAdmin = user?.role === 'super-admin';

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.getEnvoi(envoiId);
      setData(d);
      if (!objet) setObjet(`${d.envoi.concession_nom || 'Votre concession'} — suite à votre retour`);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
    // `objet` volontairement hors dépendances : il ne doit être prérempli qu'une fois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envoiId]);

  useEffect(() => { charger(); }, [charger]);

  const canalCourant = CANAUX.find((c) => c.cle === canal);
  const emet = canalCourant?.emet;

  const proposer = async () => {
    setRedaction(true);
    setErreur('');
    try {
      const { data: d } = await api.brouillon(envoiId, { canal, note_operateur: note });
      if (!d.disponible) {
        setErreur('La rédaction assistée est indisponible pour le moment — écrivez le message vous-même.');
      } else {
        setMessage(d.brouillon);
        setBrouillonIa(true);
      }
    } catch (e) {
      setErreur(e.response?.data?.error || 'Rédaction impossible');
    } finally {
      setRedaction(false);
    }
  };

  const valider = async () => {
    setEnvoi(true);
    setErreur('');
    try {
      await api.traiter(envoiId, {
        canal, note_operateur: note, objet: canal === 'mail' ? objet : null,
        message: emet ? message : null, brouillon_ia: brouillonIa,
      });
      setNote(''); setMessage(''); setBrouillonIa(false);
      await charger();
      if (onChanged) onChanged();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setEnvoi(false);
    }
  };

  const annulerTraitement = async () => {
    try {
      await api.annulerTraitement(envoiId);
      await charger();
      if (onChanged) onChanged();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Annulation impossible');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>💬 Réponse client</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {loading && <div className="sat-vide">Chargement…</div>}
          {erreur && <div className="status-error sat-bandeau">{erreur}</div>}

          {data && (
            <>
              <div className="sat-section">
                <h3>{[data.envoi.client_prenom, data.envoi.client_nom].filter(Boolean).join(' ') || 'Client'}</h3>
                <div className="sat-meta">
                  {data.envoi.formulaire_nom} · {data.envoi.concession_nom}
                  {data.envoi.site_nom && ` · ${data.envoi.site_nom}`} ·
                  {' '}envoyé le {fmtDate(data.envoi.envoye_le)} ·
                  {' '}répondu le {fmtDate(data.envoi.repondu_le)}
                  {data.envoi.traite_le && (
                    <> · <span className="status-success">
                      ✓ Traité par {CANAL_LABEL[data.envoi.canal_traitement] || '—'} le {fmtDate(data.envoi.traite_le)}
                    </span>
                    {isSuperAdmin && (
                      <button className="btn-icon" title="Annuler le marquage" onClick={annulerTraitement}>✕</button>
                    )}</>
                  )}
                </div>
              </div>

              <div className="sat-section">
                <h3>Ses réponses</h3>
                <table className="sat-reponses">
                  <tbody>
                    {data.reponses.map((r) => (
                      <tr key={r.question_id}>
                        <td>{r.libelle}</td>
                        <td>{r.type === 'recontact' ? recontactLisible(r, data.consentements) : valeurLisible(r)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data.traitements.length > 0 && (
                <div className="sat-section">
                  <h3>Suivi</h3>
                  {data.traitements.map((t) => (
                    <div key={t.id} className="sat-traitement">
                      <div className="sat-meta">
                        {CANAL_LABEL[t.canal]} · {t.user_prenom || ''} {t.user_nom || ''} · {fmtDate(t.created_at)}
                        {t.brouillon_ia && ' · ✨ rédigé avec l\'aide de l\'IA'}
                        {t.statut === 'echec' && <span className="status-error"> échec : {t.erreur}</span>}
                      </div>
                      {t.note_operateur && <div className="sat-verbatim">{t.note_operateur}</div>}
                      {t.message_envoye && (
                        <div className="sat-message">
                          {t.objet && <b>{t.objet}<br /></b>}
                          {t.message_envoye}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="sat-section">
                <h3>Répondre</h3>
                <div className="sat-variables">
                  {CANAUX.map((c) => (
                    <button key={c.cle} type="button"
                      className={`filter-chip ${canal === c.cle ? 'chip-active' : 'chip-default'}`}
                      onClick={() => { setCanal(c.cle); setBrouillonIa(false); }}>{c.label}</button>
                  ))}
                </div>

                <label className="sat-champ sat-champ-large">
                  <span>{emet ? 'Ce que vous voulez lui dire (en une phrase)' : 'Ce qui a été dit au client'}</span>
                  <textarea className="sat-input" rows={3} value={note}
                    placeholder={emet
                      ? 'ex. on le rappelle lundi, le devis était bien transmis, excuses pour l\'attente'
                      : 'ex. appelé ce matin, il était satisfait de l\'explication'}
                    onChange={(e) => setNote(e.target.value)} />
                </label>

                {emet && (
                  <>
                    <button className="btn-secondary" onClick={proposer} disabled={redaction || !note.trim()}>
                      {redaction ? 'Rédaction…' : '✨ Proposer une rédaction'}
                    </button>
                    {canal === 'mail' && (
                      <label className="sat-champ sat-champ-large">
                        <span>Objet</span>
                        <input className="sat-input" value={objet} onChange={(e) => setObjet(e.target.value)} />
                      </label>
                    )}
                    <label className="sat-champ sat-champ-large">
                      <span>
                        Message envoyé au client
                        {canal === 'sms' && ` (${message.normalize('NFD').replace(/[̀-ͯ]/g, '').length}/160)`}
                      </span>
                      <textarea className="sat-input" rows={canal === 'sms' ? 3 : 8} value={message}
                        placeholder="Relisez et corrigez avant d'envoyer."
                        onChange={(e) => setMessage(e.target.value)} />
                    </label>
                    <div className="sat-aide">
                      Destinataire : {canal === 'sms' ? (data.envoi.client_telephone || 'aucun téléphone connu')
                        : (data.envoi.client_email || 'aucun e-mail connu')}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
          <button className="btn-primary" onClick={valider}
            disabled={envoi || loading || (emet ? !message.trim() : !note.trim())}>
            {envoi ? 'Envoi…' : emet ? 'Envoyer et marquer traité' : 'Marquer traité'}
          </button>
        </div>
      </div>
    </div>
  );
}
